import { BASE_URL } from '../../../shared/lib/api';

export interface LinkSessionResponse {
  redirectURI: string;
  sessionId?: string | null;
}

export interface BrokerageConnectionsResponse {
  connections: unknown;
}

export interface BrokerageAccountsResponse {
  accounts: unknown;
}

export interface CreateSelectedBrokerageAccountInput {
  snaptrade_account_id: string;
  account_name: string;
  is_hidden: boolean;
  raw?: unknown;
}

export interface CreateSelectedBrokerageAccountsResponse {
  created_accounts: unknown[];
  created_count: number;
  recalculation_enqueued_count?: number;
}

export interface BrokeragePositionsResponse {
  positions: unknown;
}

export interface BrokerageActivitiesResponse {
  activities: unknown;
}

export interface BrokerageRawPreviewAccount {
  vester_account: {
    id?: string | null;
    account_name?: string | null;
    company?: string | null;
    brokerage_account_id?: string | null;
    brokerage_connection_id?: string | null;
    account_type?: string | null;
    account_status?: string | null;
    current_value?: number | null;
    current_value_updated_at?: string | null;
    balance_currency?: string | null;
    last_sync_time?: string | null;
    sync_status?: unknown;
    is_hidden?: boolean | null;
  };
  snaptrade_account_id: string;
  summary: {
    current_value?: number | null;
    balance_currency?: string | null;
    position_count?: number;
    tax_lot_count?: number;
    activity_count?: number;
    activity_page_count?: number;
    activity_has_more?: boolean;
    activity_truncated?: boolean;
    oldest_activity_date?: string | null;
    newest_activity_date?: string | null;
  };
  raw: {
    account_detail: unknown;
    positions: unknown;
    activities: unknown;
  };
}

export interface BrokerageRawPreviewResponse {
  generated_at: string;
  account_count: number;
  activity_limit: number;
  max_pages: number;
  accounts: BrokerageRawPreviewAccount[];
}

export interface BrokerageNormalizationPreviewAccount {
  vester_account: BrokerageRawPreviewAccount['vester_account'];
  snaptrade_account_id: string;
  summary: BrokerageRawPreviewAccount['summary'] & {
    transactions_count?: number;
    broker_activity_transactions_count?: number;
    cash_events_count?: number;
    synthetic_opening_count?: number;
    inferred_position_count?: number;
    unmapped_activities_count?: number;
    unknown_activity_transactions_count?: number;
    needs_review_count?: number;
    review_items_count?: number;
  };
  normalization: {
    transactions: unknown[];
    cash_events: unknown[];
    synthetic_opening_transactions: unknown[];
    unmapped_activities: unknown[];
    inferred_positions: unknown[];
    review_items: unknown[];
    reconciliation: unknown[];
    summary: Record<string, unknown>;
  };
}

export interface BrokerageNormalizationPreviewResponse {
  generated_at: string;
  account_count: number;
  activity_limit: number;
  max_pages: number;
  accounts: BrokerageNormalizationPreviewAccount[];
}

export interface BrokerageImportReviewedAccount {
  vester_account: BrokerageRawPreviewAccount['vester_account'];
  snaptrade_account_id: string;
  summary: BrokerageNormalizationPreviewAccount['summary'];
  upserted: {
    transactions: number;
    cash_events: number;
    account_snapshots?: number;
  };
  recalculate?: {
    ok: boolean;
    message?: string;
    impacted?: {
      account_id: string;
      equity_tickers: string[];
      option_tickers: string[];
      div_tickers: string[];
      cash_changed: boolean;
      int_changed: boolean;
    };
  };
}

export interface BrokerageImportReviewedResponse {
  generated_at: string;
  imported_count: number;
  blocked_count: number;
  accounts?: BrokerageImportReviewedAccount[];
  blocked_accounts?: Array<{
    vester_account: BrokerageRawPreviewAccount['vester_account'];
    snaptrade_account_id: string;
    summary: BrokerageNormalizationPreviewAccount['summary'];
    unmapped_activities: unknown[];
  }>;
  error?: string;
  code?: string;
}

export interface RawPreviewOptions {
  activityLimit?: number;
  maxPages?: number;
  vesterAccountIds?: string[];
  snaptradeAccountIds?: string[];
}

export interface DeleteConnectionResponse {
  success: boolean;
  connection_id: string;
  result?: unknown;
}

export interface LinkExistingBrokerageAccountResponse {
  linked_account: BrokerageRawPreviewAccount['vester_account'];
  import: BrokerageImportReviewedResponse;
}

export interface UnlinkBrokerageAccountResponse {
  success: boolean;
  account: unknown;
  deleted_local_connections?: number;
}

export interface AccountActivitiesOptions {
  offset?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  type?: string;
}

async function parseError(response: Response, fallback: string): Promise<Error> {
  const payload = await response.json().catch(() => null);
  const message = payload?.error || fallback;
  return new Error(message);
}

async function brokerageFetch<T>(
  path: string,
  token: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${BASE_URL}/brokerage${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
    credentials: 'include',
  });

  if (response.status === 401) {
    throw new Error('unauthorized');
  }
  if (!response.ok) {
    throw await parseError(response, `Brokerage request failed: ${response.status}`);
  }

  return response.json() as Promise<T>;
}

export async function createLinkSession(
  token: string,
  broker?: string
): Promise<LinkSessionResponse> {
  return brokerageFetch<LinkSessionResponse>('/link/session', token, {
    method: 'POST',
    body: JSON.stringify(broker ? { broker } : {}),
  });
}

export async function getConnections(token: string): Promise<BrokerageConnectionsResponse> {
  return brokerageFetch<BrokerageConnectionsResponse>('/connections', token);
}

export async function getAccounts(token: string): Promise<BrokerageAccountsResponse> {
  return brokerageFetch<BrokerageAccountsResponse>('/accounts', token);
}

export async function createSelectedBrokerageAccounts(
  token: string,
  broker: string,
  accounts: CreateSelectedBrokerageAccountInput[]
): Promise<CreateSelectedBrokerageAccountsResponse> {
  return brokerageFetch<CreateSelectedBrokerageAccountsResponse>('/accounts/create-selected', token, {
    method: 'POST',
    body: JSON.stringify({ broker, accounts }),
  });
}

export async function linkExistingBrokerageAccount(
  token: string,
  accountId: string,
  broker: string,
  snaptradeAccountId: string
): Promise<LinkExistingBrokerageAccountResponse> {
  return brokerageFetch<LinkExistingBrokerageAccountResponse>(
    `/accounts/${encodeURIComponent(accountId)}/link-existing`,
    token,
    {
      method: 'POST',
      body: JSON.stringify({
        broker,
        snaptrade_account_id: snaptradeAccountId,
      }),
    }
  );
}

export async function unlinkBrokerageAccount(
  token: string,
  accountId: string
): Promise<UnlinkBrokerageAccountResponse> {
  return brokerageFetch<UnlinkBrokerageAccountResponse>(
    `/accounts/${encodeURIComponent(accountId)}/unlink`,
    token,
    { method: 'POST' }
  );
}

export async function getAccountPositions(
  token: string,
  accountId: string
): Promise<BrokeragePositionsResponse> {
  return brokerageFetch<BrokeragePositionsResponse>(
    `/accounts/${encodeURIComponent(accountId)}/positions`,
    token
  );
}

export async function getAccountActivities(
  token: string,
  accountId: string,
  options: AccountActivitiesOptions = {}
): Promise<BrokerageActivitiesResponse> {
  const params = new URLSearchParams();
  Object.entries(options).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  const query = params.toString();
  return brokerageFetch<BrokerageActivitiesResponse>(
    `/accounts/${encodeURIComponent(accountId)}/activities${query ? `?${query}` : ''}`,
    token
  );
}

export async function getRawAccountPreview(
  token: string,
  options: RawPreviewOptions = {}
): Promise<BrokerageRawPreviewResponse> {
  const params = new URLSearchParams();
  Object.entries(options).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item) params.append(key, item);
      });
    } else if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  const query = params.toString();
  return brokerageFetch<BrokerageRawPreviewResponse>(
    `/accounts/raw-preview${query ? `?${query}` : ''}`,
    token
  );
}

export async function getNormalizationPreview(
  token: string,
  options: RawPreviewOptions = {}
): Promise<BrokerageNormalizationPreviewResponse> {
  const params = new URLSearchParams();
  Object.entries(options).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item) params.append(key, item);
      });
    } else if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  const query = params.toString();
  return brokerageFetch<BrokerageNormalizationPreviewResponse>(
    `/accounts/normalization-preview${query ? `?${query}` : ''}`,
    token
  );
}

export async function importReviewedBrokerageData(
  token: string,
  options: RawPreviewOptions = {}
): Promise<BrokerageImportReviewedResponse> {
  return brokerageFetch<BrokerageImportReviewedResponse>('/accounts/import-reviewed', token, {
    method: 'POST',
    body: JSON.stringify({
      activityLimit: options.activityLimit ?? 1000,
      maxPages: options.maxPages ?? 50,
      vesterAccountIds: options.vesterAccountIds,
      snaptradeAccountIds: options.snaptradeAccountIds,
    }),
  });
}

export async function deleteConnection(
  token: string,
  connectionId: string
): Promise<DeleteConnectionResponse> {
  return brokerageFetch<DeleteConnectionResponse>(
    `/connections/${encodeURIComponent(connectionId)}`,
    token,
    { method: 'DELETE' }
  );
}
